import { useState, useEffect, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { ScrollArea } from "./ui/scroll-area";
import { 
  Users, Plus, Copy, Check, X, Sparkles, Moon, Heart, 
  Send, Clock, Calendar, Play, MessageCircle, Target,
  Crown, UserPlus, Settings, ArrowLeft, Timer, Trophy
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Reaction emojis for intentions
const REACTION_EMOJIS = ["🌟", "💫", "✨", "🙏🏾", "💜", "🌙", "🦋", "🌻"];

const ManifestationCircles = ({ isOpen, onClose }) => {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [circles, setCircles] = useState([]);
  const [selectedCircle, setSelectedCircle] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showJoin, setShowJoin] = useState(false);
  const [activeTab, setActiveTab] = useState("intentions");
  
  // Form states
  const [newCircleName, setNewCircleName] = useState("");
  const [newCircleDesc, setNewCircleDesc] = useState("");
  const [joinCode, setJoinCode] = useState("");
  const [newIntention, setNewIntention] = useState("");
  const [chatMessage, setChatMessage] = useState("");
  const [copied, setCopied] = useState(false);
  const [creating, setCreating] = useState(false);
  const [joining, setJoining] = useState(false);
  
  // Session states
  const [activeSession, setActiveSession] = useState(null);
  const [sessionTimer, setSessionTimer] = useState(0);
  const [meditationDuration, setMeditationDuration] = useState(10);
  const [leaderboard, setLeaderboard] = useState(null);
  
  const chatEndRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    if (isOpen && token) {
      fetchCircles();
    }
  }, [isOpen, token]);

  useEffect(() => {
    // Scroll to bottom of chat when messages update
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [selectedCircle?.messages]);

  useEffect(() => {
    // Session timer
    if (activeSession?.is_active) {
      timerRef.current = setInterval(() => {
        const endTime = new Date(activeSession.ends_at);
        const now = new Date();
        const remaining = Math.max(0, Math.floor((endTime - now) / 1000));
        setSessionTimer(remaining);
        
        if (remaining === 0) {
          clearInterval(timerRef.current);
          setActiveSession(null);
          toast.success("Session complete! 🧘🏾 Great manifestation work!");
        }
      }, 1000);
      
      return () => clearInterval(timerRef.current);
    }
  }, [activeSession]);

  const fetchCircles = async () => {
    try {
      const res = await axios.get(`${API}/circles/my-circles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCircles(res.data.circles || []);
    } catch (error) {
      console.error("Error fetching circles:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCircleDetails = async (circleId) => {
    try {
      const res = await axios.get(`${API}/circles/${circleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedCircle(res.data);
      
      // Check for active sessions
      const activeSessions = res.data.sessions?.filter(s => s.is_active);
      if (activeSessions?.length > 0) {
        setActiveSession(activeSessions[0]);
      }
      
      // Fetch leaderboard
      fetchLeaderboard(circleId);
    } catch (error) {
      toast.error("Failed to load circle details");
    }
  };

  const fetchLeaderboard = async (circleId) => {
    try {
      const res = await axios.get(`${API}/circles/${circleId}/leaderboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeaderboard(res.data);
    } catch (error) {
      console.error("Failed to fetch leaderboard:", error);
    }
  };

  const createCircle = async () => {
    if (!newCircleName.trim()) {
      toast.error("Please enter a circle name");
      return;
    }
    
    setCreating(true);
    try {
      const res = await axios.post(
        `${API}/circles/create`,
        { name: newCircleName, description: newCircleDesc },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (res.data.success) {
        toast.success(res.data.message);
        setShowCreate(false);
        setNewCircleName("");
        setNewCircleDesc("");
        fetchCircles();
      }
    } catch (error) {
      toast.error("Failed to create circle");
    } finally {
      setCreating(false);
    }
  };

  const joinCircle = async () => {
    if (!joinCode.trim()) {
      toast.error("Please enter an invite code");
      return;
    }
    
    setJoining(true);
    try {
      const res = await axios.post(
        `${API}/circles/join`,
        { invite_code: joinCode.toUpperCase() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (res.data.success) {
        toast.success(res.data.message);
        setShowJoin(false);
        setJoinCode("");
        fetchCircles();
      } else {
        toast.error(res.data.error);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to join circle");
    } finally {
      setJoining(false);
    }
  };

  const leaveCircle = async (circleId) => {
    if (!confirm("Are you sure you want to leave this circle?")) return;
    
    try {
      await axios.delete(`${API}/circles/${circleId}/leave`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("You've left the circle");
      setSelectedCircle(null);
      fetchCircles();
    } catch (error) {
      toast.error("Failed to leave circle");
    }
  };

  const addIntention = async () => {
    if (!newIntention.trim()) return;
    
    try {
      const res = await axios.post(
        `${API}/circles/${selectedCircle.id}/intentions`,
        { text: newIntention },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (res.data.success) {
        toast.success(res.data.message);
        setNewIntention("");
        fetchCircleDetails(selectedCircle.id);
      } else {
        toast.error(res.data.error);
      }
    } catch (error) {
      toast.error("Failed to add intention");
    }
  };

  const markManifested = async (intentionId) => {
    try {
      const res = await axios.put(
        `${API}/circles/${selectedCircle.id}/intentions/${intentionId}/manifest`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (res.data.success) {
        toast.success(res.data.message);
        fetchCircleDetails(selectedCircle.id);
      }
    } catch (error) {
      toast.error("Failed to mark as manifested");
    }
  };

  const reactToIntention = async (intentionId, emoji) => {
    try {
      await axios.post(
        `${API}/circles/${selectedCircle.id}/intentions/${intentionId}/react`,
        { emoji },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchCircleDetails(selectedCircle.id);
    } catch (error) {
      console.error("Failed to react");
    }
  };

  const sendMessage = async () => {
    if (!chatMessage.trim()) return;
    
    try {
      await axios.post(
        `${API}/circles/${selectedCircle.id}/messages`,
        { message: chatMessage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setChatMessage("");
      fetchCircleDetails(selectedCircle.id);
    } catch (error) {
      toast.error("Failed to send message");
    }
  };

  const startDropInSession = async () => {
    try {
      const res = await axios.post(
        `${API}/circles/${selectedCircle.id}/sessions/start`,
        { duration: meditationDuration },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (res.data.success) {
        toast.success(res.data.message);
        setActiveSession(res.data.session);
        fetchCircleDetails(selectedCircle.id);
      }
    } catch (error) {
      toast.error("Failed to start session");
    }
  };

  const copyInviteCode = () => {
    navigator.clipboard.writeText(selectedCircle?.invite_code || "");
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const formatTimer = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-hidden p-0">
        {/* Header */}
        <DialogHeader className="p-4 pb-2 border-b border-border">
          <div className="flex items-center gap-2">
            {selectedCircle && (
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-8 w-8"
                onClick={() => setSelectedCircle(null)}
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
            )}
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <DialogTitle className="text-lg">
                {selectedCircle ? selectedCircle.name : "Manifestation Circles"}
              </DialogTitle>
              <DialogDescription className="text-xs">
                {selectedCircle 
                  ? `${selectedCircle.members?.length || 0} members manifesting together`
                  : "Collective intention setting & group meditation"
                }
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <ScrollArea className="h-[calc(90vh-80px)]">
          <div className="p-4">
            {!selectedCircle ? (
              // Circle List View
              <>
                {/* Action Buttons */}
                <div className="grid grid-cols-2 gap-2 mb-4">
                  <Button 
                    onClick={() => setShowCreate(true)}
                    className="bg-gradient-to-r from-violet-500 to-purple-600"
                    data-testid="create-circle-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Create Circle
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => setShowJoin(true)}
                    data-testid="join-circle-btn"
                  >
                    <UserPlus className="w-4 h-4 mr-1" />
                    Join Circle
                  </Button>
                </div>

                {/* Circles List */}
                {loading ? (
                  <div className="text-center py-8 text-muted-foreground">Loading circles...</div>
                ) : circles.length === 0 ? (
                  <Card className="bg-gradient-to-br from-violet-500/10 to-purple-500/10 border-violet-500/30">
                    <CardContent className="p-6 text-center">
                      <Sparkles className="w-12 h-12 mx-auto mb-3 text-violet-400/50" />
                      <h3 className="font-semibold mb-2">No Circles Yet</h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        Create a circle to manifest with your community, or join one with an invite code!
                      </p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-2">
                    {circles.map((circle) => (
                      <Card 
                        key={circle.id}
                        className="cursor-pointer hover:border-violet-500/50 transition-colors"
                        onClick={() => fetchCircleDetails(circle.id)}
                        data-testid={`circle-card-${circle.id}`}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-500/20 to-purple-500/20 flex items-center justify-center">
                                <Users className="w-5 h-5 text-violet-500" />
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <h4 className="font-medium">{circle.name}</h4>
                                  {circle.is_creator && (
                                    <Crown className="w-3 h-3 text-amber-500" />
                                  )}
                                </div>
                                <p className="text-xs text-muted-foreground">
                                  {circle.member_count} member{circle.member_count !== 1 ? 's' : ''} • {circle.intention_count} intention{circle.intention_count !== 1 ? 's' : ''}
                                </p>
                              </div>
                            </div>
                            {circle.upcoming_sessions > 0 && (
                              <Badge variant="secondary" className="text-xs">
                                {circle.upcoming_sessions} session{circle.upcoming_sessions !== 1 ? 's' : ''}
                              </Badge>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}

                {/* Create Circle Modal */}
                {showCreate && (
                  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <Card className="w-full max-w-sm">
                      <CardContent className="p-6">
                        <h3 className="font-semibold text-lg mb-4">Create a Circle</h3>
                        <div className="space-y-4">
                          <div>
                            <label className="text-sm font-medium">Circle Name</label>
                            <Input
                              value={newCircleName}
                              onChange={(e) => setNewCircleName(e.target.value)}
                              placeholder="e.g., Full Moon Manifesters"
                              maxLength={50}
                              data-testid="circle-name-input"
                            />
                          </div>
                          <div>
                            <label className="text-sm font-medium">Description (optional)</label>
                            <Textarea
                              value={newCircleDesc}
                              onChange={(e) => setNewCircleDesc(e.target.value)}
                              placeholder="What will your circle manifest?"
                              maxLength={200}
                              className="min-h-[80px]"
                            />
                          </div>
                          <div className="flex gap-2">
                            <Button variant="outline" onClick={() => setShowCreate(false)} className="flex-1">
                              Cancel
                            </Button>
                            <Button onClick={createCircle} disabled={creating} className="flex-1 bg-violet-500 hover:bg-violet-600" data-testid="confirm-create-circle-btn">
                              {creating ? "Creating..." : "Create"}
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* Join Circle Modal */}
                {showJoin && (
                  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <Card className="w-full max-w-sm">
                      <CardContent className="p-6">
                        <h3 className="font-semibold text-lg mb-4">Join a Circle</h3>
                        <div className="space-y-4">
                          <div>
                            <label className="text-sm font-medium">Invite Code</label>
                            <Input
                              value={joinCode}
                              onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                              placeholder="Enter 6-character code"
                              maxLength={6}
                              className="text-center text-lg tracking-widest font-mono"
                              data-testid="join-code-input"
                            />
                          </div>
                          <div className="flex gap-2">
                            <Button variant="outline" onClick={() => setShowJoin(false)} className="flex-1">
                              Cancel
                            </Button>
                            <Button onClick={joinCircle} disabled={joining} className="flex-1 bg-violet-500 hover:bg-violet-600" data-testid="confirm-join-circle-btn">
                              {joining ? "Joining..." : "Join"}
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                )}
              </>
            ) : (
              // Circle Detail View
              <>
                {/* Active Session Banner */}
                {activeSession?.is_active && (
                  <Card className="mb-4 bg-gradient-to-r from-violet-500 to-purple-600 text-white border-0">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center animate-pulse">
                            <Timer className="w-6 h-6" />
                          </div>
                          <div>
                            <p className="font-semibold">Session in Progress</p>
                            <p className="text-sm text-white/80">{activeSession.participants?.length || 1} manifesting</p>
                          </div>
                        </div>
                        <div className="text-2xl font-bold font-mono">
                          {formatTimer(sessionTimer)}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Invite Code */}
                <Card className="mb-4 bg-violet-50 dark:bg-violet-500/10 border-violet-200 dark:border-violet-500/30">
                  <CardContent className="p-3 flex items-center justify-between">
                    <div>
                      <p className="text-xs text-muted-foreground">Invite Code</p>
                      <p className="font-mono font-bold text-lg tracking-widest">{selectedCircle.invite_code}</p>
                    </div>
                    <Button variant="outline" size="sm" onClick={copyInviteCode}>
                      {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </CardContent>
                </Card>

                {/* Tabs */}
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                  <TabsList className="grid w-full grid-cols-4 mb-4">
                    <TabsTrigger value="intentions">
                      <Target className="w-4 h-4 mr-1" />
                      <span className="hidden sm:inline">Intentions</span>
                    </TabsTrigger>
                    <TabsTrigger value="leaderboard">
                      <Trophy className="w-4 h-4 mr-1" />
                      <span className="hidden sm:inline">Board</span>
                    </TabsTrigger>
                    <TabsTrigger value="meditate">
                      <Moon className="w-4 h-4 mr-1" />
                      <span className="hidden sm:inline">Meditate</span>
                    </TabsTrigger>
                    <TabsTrigger value="chat">
                      <MessageCircle className="w-4 h-4 mr-1" />
                      <span className="hidden sm:inline">Chat</span>
                    </TabsTrigger>
                  </TabsList>

                  {/* Intentions Tab */}
                  <TabsContent value="intentions" className="space-y-3">
                    {/* Add Intention */}
                    <div className="flex gap-2">
                      <Input
                        value={newIntention}
                        onChange={(e) => setNewIntention(e.target.value)}
                        placeholder="Set your intention..."
                        maxLength={500}
                        onKeyDown={(e) => e.key === 'Enter' && addIntention()}
                        data-testid="intention-input"
                      />
                      <Button onClick={addIntention} className="bg-violet-500 hover:bg-violet-600" data-testid="add-intention-btn">
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>

                    {/* Intentions List */}
                    <div className="space-y-2 max-h-[300px] overflow-y-auto">
                      {selectedCircle.intentions?.length === 0 ? (
                        <p className="text-center text-muted-foreground py-4 text-sm">
                          No intentions yet. Be the first to set one! 🌟
                        </p>
                      ) : (
                        selectedCircle.intentions?.slice().reverse().map((intention) => (
                          <Card key={intention.id} className={`${intention.manifested ? 'bg-green-50 dark:bg-green-500/10 border-green-300' : ''}`}>
                            <CardContent className="p-3">
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-muted-foreground">{intention.user_name}</p>
                                  <p className={`text-sm ${intention.manifested ? 'line-through text-green-600' : ''}`}>
                                    {intention.text}
                                  </p>
                                  {intention.manifested && (
                                    <Badge variant="secondary" className="mt-1 text-xs bg-green-100 text-green-700">
                                      ✨ Manifested!
                                    </Badge>
                                  )}
                                </div>
                                {intention.user_id === user?.id && !intention.manifested && (
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    onClick={() => markManifested(intention.id)}
                                    className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                  >
                                    <Check className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                              {/* Reactions */}
                              <div className="flex items-center gap-1 mt-2 flex-wrap">
                                {REACTION_EMOJIS.map((emoji) => {
                                  const count = intention.reactions?.filter(r => r.emoji === emoji).length || 0;
                                  const hasReacted = intention.reactions?.some(r => r.user_id === user?.id && r.emoji === emoji);
                                  return (
                                    <button
                                      key={emoji}
                                      onClick={() => reactToIntention(intention.id, emoji)}
                                      className={`text-sm px-1.5 py-0.5 rounded-full transition-colors ${
                                        hasReacted ? 'bg-violet-100 dark:bg-violet-500/30' : 'hover:bg-muted'
                                      }`}
                                    >
                                      {emoji} {count > 0 && <span className="text-xs">{count}</span>}
                                    </button>
                                  );
                                })}
                              </div>
                            </CardContent>
                          </Card>
                        ))
                      )}
                    </div>
                  </TabsContent>

                  {/* Leaderboard Tab */}
                  <TabsContent value="leaderboard" className="space-y-3">
                    <Card className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 border-amber-500/30">
                      <CardContent className="p-4 text-center">
                        <Trophy className="w-10 h-10 mx-auto mb-2 text-amber-500" />
                        <h3 className="font-semibold text-lg">Manifestation Leaderboard</h3>
                        <p className="text-xs text-muted-foreground">
                          {leaderboard?.total_manifested || 0} of {leaderboard?.total_intentions || 0} intentions manifested! ✨
                        </p>
                      </CardContent>
                    </Card>

                    {/* Leaderboard List */}
                    <div className="space-y-2 max-h-[300px] overflow-y-auto">
                      {!leaderboard?.leaderboard?.length ? (
                        <p className="text-center text-muted-foreground py-4 text-sm">
                          No manifested intentions yet. Set intentions and mark them as manifested to climb the board! 🌟
                        </p>
                      ) : (
                        leaderboard.leaderboard.map((entry) => (
                          <Card 
                            key={entry.user_id}
                            className={`${entry.rank === 1 ? 'bg-gradient-to-r from-amber-500/20 to-yellow-500/20 border-amber-400' : 
                                       entry.rank === 2 ? 'bg-gradient-to-r from-slate-400/20 to-gray-400/20 border-slate-400' :
                                       entry.rank === 3 ? 'bg-gradient-to-r from-orange-600/20 to-amber-600/20 border-orange-400' : ''}`}
                          >
                            <CardContent className="p-3 flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold
                                  ${entry.rank === 1 ? 'bg-amber-500 text-white' :
                                    entry.rank === 2 ? 'bg-slate-400 text-white' :
                                    entry.rank === 3 ? 'bg-orange-600 text-white' :
                                    'bg-muted text-muted-foreground'}`}>
                                  {entry.rank}
                                </div>
                                <div>
                                  <p className="font-medium flex items-center gap-1">
                                    {entry.name}
                                    <span className="text-lg">{entry.badge}</span>
                                    {entry.user_id === user?.id && (
                                      <Badge variant="secondary" className="text-[10px] ml-1">You</Badge>
                                    )}
                                  </p>
                                  <p className="text-xs text-muted-foreground">
                                    {entry.manifested_count} manifested of {entry.total_intentions} intentions
                                  </p>
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="text-lg font-bold text-amber-600">{entry.manifested_count}</p>
                                <p className="text-[10px] text-muted-foreground">manifested</p>
                              </div>
                            </CardContent>
                          </Card>
                        ))
                      )}
                    </div>

                    {/* Badge Legend */}
                    <div className="mt-4 p-3 bg-muted/50 rounded-lg">
                      <p className="text-xs font-medium mb-2">Manifestation Badges:</p>
                      <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                        <span>🏆 Master Manifester (10+)</span>
                        <span>⭐ Rising Star (5+)</span>
                        <span>✨ Manifester (1+)</span>
                        <span>🌱 Seedling (0)</span>
                      </div>
                    </div>
                  </TabsContent>

                  {/* Meditate Tab */}
                  <TabsContent value="meditate" className="space-y-4">
                    {!activeSession?.is_active ? (
                      <Card className="bg-gradient-to-br from-violet-500/10 to-purple-500/10 border-violet-500/30">
                        <CardContent className="p-6 text-center">
                          <Moon className="w-16 h-16 mx-auto mb-4 text-violet-400" />
                          <h3 className="font-semibold text-lg mb-2">Start a Group Session</h3>
                          <p className="text-sm text-muted-foreground mb-4">
                            Meditate and manifest together with your circle
                          </p>
                          <div className="flex items-center justify-center gap-2 mb-4">
                            <label className="text-sm">Duration:</label>
                            <select 
                              value={meditationDuration}
                              onChange={(e) => setMeditationDuration(Number(e.target.value))}
                              className="bg-background border rounded px-2 py-1 text-sm"
                            >
                              <option value={5}>5 min</option>
                              <option value={10}>10 min</option>
                              <option value={15}>15 min</option>
                              <option value={20}>20 min</option>
                              <option value={30}>30 min</option>
                            </select>
                          </div>
                          <Button 
                            onClick={startDropInSession}
                            className="bg-gradient-to-r from-violet-500 to-purple-600"
                            data-testid="start-session-btn"
                          >
                            <Play className="w-4 h-4 mr-2" />
                            Start Session
                          </Button>
                        </CardContent>
                      </Card>
                    ) : (
                      <Card className="bg-gradient-to-br from-violet-900 to-purple-900 text-white border-0">
                        <CardContent className="p-8 text-center">
                          <div className="w-24 h-24 mx-auto mb-4 rounded-full bg-white/10 flex items-center justify-center">
                            <Moon className="w-12 h-12 animate-pulse" />
                          </div>
                          <p className="text-4xl font-bold font-mono mb-2">
                            {formatTimer(sessionTimer)}
                          </p>
                          <p className="text-white/70 mb-4">
                            Breathe deeply. Focus on your intentions.
                          </p>
                          <div className="flex justify-center gap-2">
                            {selectedCircle.members?.slice(0, 5).map((member, i) => (
                              <div 
                                key={member.user_id}
                                className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-xs font-medium"
                                title={member.name}
                              >
                                {member.name?.charAt(0) || "?"}
                              </div>
                            ))}
                            {(selectedCircle.members?.length || 0) > 5 && (
                              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-xs">
                                +{selectedCircle.members.length - 5}
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    )}
                  </TabsContent>

                  {/* Chat Tab */}
                  <TabsContent value="chat" className="space-y-3">
                    <div className="h-[250px] overflow-y-auto space-y-2 p-2 bg-muted/30 rounded-lg">
                      {selectedCircle.messages?.length === 0 ? (
                        <p className="text-center text-muted-foreground py-4 text-sm">
                          No messages yet. Start the conversation! 💬
                        </p>
                      ) : (
                        selectedCircle.messages?.map((msg) => (
                          <div 
                            key={msg.id}
                            className={`flex ${msg.user_id === user?.id ? 'justify-end' : 'justify-start'}`}
                          >
                            <div className={`max-w-[80%] p-2 rounded-lg ${
                              msg.user_id === user?.id 
                                ? 'bg-violet-500 text-white' 
                                : 'bg-background border'
                            }`}>
                              {msg.user_id !== user?.id && (
                                <p className="text-xs font-medium text-muted-foreground mb-1">{msg.user_name}</p>
                              )}
                              <p className="text-sm">{msg.message}</p>
                            </div>
                          </div>
                        ))
                      )}
                      <div ref={chatEndRef} />
                    </div>
                    <div className="flex gap-2">
                      <Input
                        value={chatMessage}
                        onChange={(e) => setChatMessage(e.target.value)}
                        placeholder="Send a message..."
                        maxLength={1000}
                        onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
                        data-testid="chat-input"
                      />
                      <Button onClick={sendMessage} className="bg-violet-500 hover:bg-violet-600" data-testid="send-chat-btn">
                        <Send className="w-4 h-4" />
                      </Button>
                    </div>
                  </TabsContent>
                </Tabs>

                {/* Leave Circle Button */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => leaveCircle(selectedCircle.id)}
                  className="w-full mt-4 text-red-500 hover:text-red-600 hover:bg-red-50"
                >
                  <X className="w-4 h-4 mr-1" />
                  Leave Circle
                </Button>
              </>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default ManifestationCircles;
